import { call, put, takeLatest } from 'redux-saga/effects';

import * as actions from 'features/auth/redux/actions';
import * as userActions from 'features/user/redux/actions';
import * as types from 'features/auth/redux/types';
import { authPaths } from 'features/auth/routes/paths';
import {
  autologin,
  confirmRegistration,
  confirmResetPassword,
  login,
  logout,
  resetPassword,
  uploadVerificationDocuments,
  loginByVerificationCode as loginByVerificationCodeAPICall,
} from 'features/auth/api';
import { addErrorMessage, addSuccessMessage } from 'features/message/redux/action';
import { clearAuthData, saveToken } from 'utils/localStorage';
import { ErrorUtils } from 'utils';
import { t } from 'locales/i18n';
import { getProfileRequest } from 'features/user/api';
import { AxiosResponse } from 'axios';
import { PartnerUser } from 'features/users/ts/PartnerUser';
import { RegistrationRequestStatusType } from 'features/auth/ts';

function* loginSaga(action: ReturnType<typeof actions.loginRequest>) {
  const { email, password } = action.payload;
  try {
    yield call(login, email, password);
    action.payload.successCallback();
    yield put(actions.loginFailed(null));
  } catch (error) {
    const errMsg = ErrorUtils.convertError(error);
    yield put(actions.loginFailed(errMsg));

    yield put(addErrorMessage(errMsg));
  }
}

function* loginByVerificationCode(action: ReturnType<typeof actions.loginByVerificationCodeRequest>) {
  try {
    const {
      data: { token, user, is_first_login },
    } = yield call(loginByVerificationCodeAPICall, action.payload.token);
    yield saveToken(token);
    yield put(actions.loginSuccess(user, is_first_login));

    yield put(addSuccessMessage(t('messages.loginSuccess', { ns: 'auth' })));

    if (user && user?.registration_request_status === RegistrationRequestStatusType.PENDING_ACTION) {
      yield put(actions.showUploadVerificationDocumentsModal(true));
    }
  } catch (error) {
    const errMsg = ErrorUtils.convertError(error);
    yield put(actions.loginByVerificationCodeFailed(errMsg));

    yield put(addErrorMessage(errMsg));
  }
}

function* autologinSaga(action: ReturnType<typeof actions.autologinRequest>) {
  const { autologin_token } = action.payload;
  try {
    const {
      data: { token, user, is_first_login },
    } = yield call(autologin, autologin_token);

    yield saveToken(token);
    yield put(actions.loginSuccess(user, is_first_login));
    yield put(addSuccessMessage(t('messages.loginSuccess', { ns: 'auth' })));
    if (user && user?.registration_request_status === RegistrationRequestStatusType.PENDING_ACTION) {
      yield put(actions.showUploadVerificationDocumentsModal(true));
    }
  } catch (error) {
    window.location.href = `${window.location.origin}${authPaths.static.login}`;
  }
}

function* logoutSaga() {
  try {
    yield call(logout);
    yield clearAuthData();
    yield put(actions.logoutSuccess());
  } catch (error) {
    yield put(actions.logoutFailed(ErrorUtils.convertError(error)));
  }
}

function* resetPasswordSaga(action: ReturnType<typeof actions.resetPasswordRequest>) {
  const { email } = action.payload;
  try {
    const {
      data: { detail },
    } = yield call(resetPassword, email);
    yield put(actions.resetPasswordSuccess());

    yield put(addSuccessMessage(detail || t('messages.emailSent', { ns: 'auth' })));
  } catch (error) {
    const errMsg = ErrorUtils.convertError(error);
    yield put(actions.resetPasswordFailed(errMsg));

    yield put(addErrorMessage(`${t('messages.resetPasswordFailed', { ns: 'auth' })}: ${errMsg}`));
  }
}

function* confirmResetPasswordSaga(action: ReturnType<typeof actions.confirmResetPasswordRequest>) {
  const { token, new_password } = action.payload;
  try {
    yield call(confirmResetPassword, token, new_password);

    yield put(actions.confirmResetPasswordSuccess());

    yield put(addSuccessMessage(t('messages.successSetUpPassword', { ns: 'auth' })));

    window.location.href = `${window.location.origin}${authPaths.static.login}`;
  } catch (error) {
    const errMsg = ErrorUtils.accumulateErrorMessage(error) || error?.response?.data?.[0];
    yield put(actions.confirmResetPasswordFailed(errMsg));

    yield put(addErrorMessage(`${t('messages.resetPasswordFailed', { ns: 'auth' })}: ${errMsg}`));
  }
}

function* confirmRegistrationSaga(action: ReturnType<typeof actions.confirmRegistrationRequest>) {
  const { token, new_password } = action.payload;
  try {
    const {
      data: { token: authToken, user, is_first_login, is_first_partner_super_admin },
    } = yield call(confirmRegistration, token, new_password);
    yield put(actions.confirmRegistrationSuccess());

    yield saveToken(authToken);
    yield put(actions.loginSuccess(user, is_first_login));
    if (is_first_partner_super_admin) {
      yield put(actions.showUploadVerificationDocumentsModal());
    }

    yield put(addSuccessMessage(t('messages.passwordCreated', { ns: 'auth' })));
  } catch (error) {
    const errMsg = ErrorUtils.accumulateErrorMessage(error);
    yield put(actions.confirmRegistrationFailed(errMsg));

    yield put(addErrorMessage(errMsg));
  }
}

function* uploadVerificationDocumentsSaga(action: ReturnType<typeof actions.uploadVerificationDocumentsRequest>) {
  const { personal_id, trade_license } = action.payload;
  try {
    const formData = new FormData();
    formData.append('personal_id', personal_id);
    formData.append('trade_license', trade_license);

    yield call(uploadVerificationDocuments, formData);

    yield put(actions.uploadVerificationDocumentsSuccess());
    yield put(actions.hideUploadVerificationDocumentsModal());

    const { data: user } = (yield call(getProfileRequest)) as AxiosResponse<PartnerUser>;
    yield put(userActions.profileSuccess(user));

    yield put(addSuccessMessage(t('messages.documentsUploaded', { ns: 'auth' })));
  } catch (error) {
    const errMsg = ErrorUtils.accumulateErrorMessage(error);
    yield put(actions.uploadVerificationDocumentsFailed(errMsg));

    yield put(addErrorMessage(errMsg));
  }
}

function* authSaga() {
  yield takeLatest(types.LOGIN_REQUEST, loginSaga);
  yield takeLatest(types.LOGIN_BY_VERIFICATION_CODE_REQUEST, loginByVerificationCode);
  yield takeLatest(types.AUTO_LOGIN_REQUEST, autologinSaga);
  yield takeLatest(types.LOGOUT_REQUEST, logoutSaga);
  yield takeLatest(types.RESET_PASSWORD_REQUEST, resetPasswordSaga);
  yield takeLatest(types.CONFIRM_RESET_PASSWORD_REQUEST, confirmResetPasswordSaga);
  yield takeLatest(types.CONFIRM_REGISTRATION_REQUEST, confirmRegistrationSaga);
  yield takeLatest(types.UPLOAD_VERIFICATION_DOCUMENTS_REQUEST, uploadVerificationDocumentsSaga);
}

export default authSaga;
